import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, CardContent, TextField, Grid, MenuItem, Typography, CardActions } from '@material-ui/core';
import { ThemeWrapper } from '../common/ThemeWrapper';
import { HoseSeries } from '../../constants/HoseSeries';
import { getOr, flowRight, path } from 'lodash/fp';
import { findByValue } from '../../utils/lookups';
import { ContentContainer } from '../common/ContentContainer';
import { useStyles, useLabelStyles } from '../common/styles';

export const PressureCalculator = () => {
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [clonedModel, setClonedModel] = useState({ carriers: '', wires: '', wireDia: '', angle: '', tensile: '', layers: '' });
  const [burst, setBurst] = useState('');
  const [test, setTest] = useState('');
  const [working, setWorking] = useState('');
  const [weight, setWeight] = useState('');
  const [coverage, setCoverage] = useState('');
  const [capacity, setCapacity] = useState('');

  const productChoices = useMemo(
    () => flowRight(
      getOr([], 'products'),
      findByValue(selectedProduct)
    )(HoseSeries), 
    [selectedProduct]
  );

  const modelInfo = useMemo(
    () => findByValue(selectedModel)(productChoices), 
    [selectedModel, productChoices]
  );

  const imgSrc = flowRight(
    getOr('', 'image'),
    findByValue(selectedProduct)
  )(HoseSeries);

  useEffect(() => {
    setClonedModel(modelInfo)
  }, [modelInfo])

  const handleHoseSelection = (e) => { 
    e.persist();
    setSelectedProduct(e.target.value);
  }

  const handleModelSelection = (e) => {
    e.persist();
    setSelectedModel(e.target.value);
  }

  const onCarriersChange = (onChangeEventObject) => { 
    onChangeEventObject.persist();
    setClonedModel((preValue) => {
      return {...preValue, carriers: onChangeEventObject.target.value}
    });
  }

  const onWiresChange = (onChangeEventObject) => { 
    onChangeEventObject.persist();
    setClonedModel((preValue) => {
      return {...preValue, wires: onChangeEventObject.target.value}
    });
  }

  const onWireDiaChange = (onChangeEventObject) => { 
    onChangeEventObject.persist();
    setClonedModel((preValue) => {
      return {...preValue, wireDia: onChangeEventObject.target.value}
    });
  }

  const onAngleChange = (onChangeEventObject) => { 
    onChangeEventObject.persist();
    setClonedModel((preValue) => {
      return {...preValue, angle: onChangeEventObject.target.value}
    });
  }

  const onTensileChange = (onChangeEventObject) => { 
    onChangeEventObject.persist();
    setClonedModel((preValue) => {
      return {...preValue, tensile: onChangeEventObject.target.value}
    });
  }

  const onLayersChange = (onChangeEventObject) => { 
    onChangeEventObject.persist();
    setClonedModel((preValue) => {
      return {...preValue, layers: onChangeEventObject.target.value}
    });
  }

  const reset = useCallback(() => {
    setSelectedProduct();
    setSelectedModel();
    setClonedModel(
      { carriers: '', wires: '', wireDia: '', angle: '', tensile: '', layers: '' }
    );
    setBurst('');
    setTest('');
    setWorking('');
    setWeight('');
    setCoverage('');
    setCapacity('');
  }, [setSelectedProduct,
    setSelectedModel,
    setClonedModel,
    setBurst,
    setTest,
    setWorking,
    setWeight,
    setCoverage,
    setCapacity,
  ]);

  const recalculate = useCallback(() => {
    if(clonedModel === undefined) { return; }
    // xCalculated are used for all calculations for accuracy
    // xRounded is what is ultimately displayed to the user
    // If you round calculated numbers and use them for later calculations, you're going to have a bad time
    var weightCalculated = (2.733 * clonedModel.carriers * clonedModel.wires * clonedModel.wireDia * clonedModel.wireDia) / Math.cos (clonedModel.angle * Math.PI /180);
    var weightRounded = weightCalculated.toPrecision(2);
    var capacityCalculated = (clonedModel.carriers * clonedModel.wires * clonedModel.wireDia )/(2* Math.cos (clonedModel.angle * Math.PI /180)* (clonedModel.OD + 2 * clonedModel.wireDia)* Math.PI );
    var capacityRounded = (capacityCalculated.toPrecision(2))*100;
    var coverageCalculated = 1 - (Math.pow (1-capacityCalculated,2));
    var coverageRounded = (coverageCalculated.toPrecision(2))*100;
    
    var wireTenStr = clonedModel.carriers * clonedModel.wires * Math.pow (clonedModel.wireDia/2,2)* Math.PI * clonedModel.tensile * 1000 * Math.cos (clonedModel.angle * Math.PI /180);
    var crossSectionArea = Math.pow ((clonedModel.OD + clonedModel.ID)/4,2)* Math.PI;
    var burstPressure;
    if(clonedModel.layers == 1){
      burstPressure = (wireTenStr / crossSectionArea);
    }else if(clonedModel.layers == 2){
      burstPressure = (wireTenStr / crossSectionArea) * 1.6;
      weightCalculated = (weightCalculated * 2).toPrecision(2);
    }else{
      alert('We are sorry, but at this time we braid\nhose with only 2 layers of braid.');
      return;
    }
    var burstPressureRounded = Math.round(burstPressure);
    var testPressure = 0.375 * burstPressure;
    var testPressureRounded = Math.round(testPressure);
    var workPressure = 0.25 * burstPressure;
    var workPressureRounded = Math.round(workPressure);
    setBurst(burstPressureRounded);
    setTest(testPressureRounded);
    setWorking(workPressureRounded);
    setWeight(weightRounded);
    setCoverage(coverageRounded);
    setCapacity(capacityRounded);
  }, [clonedModel]);
  
  const classes = useStyles();
  const inputProps = {
      classes:{
          root: classes.inputRoot,
          disabled: classes.disabled
      }
  };
  
  const labelClasses = useLabelStyles();
  const labelProps = {
      classes:{
          root: labelClasses.root,
      }
  };
  return (
    <ThemeWrapper title={'Pressure Calculator'}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField 
              select
              label={'Choose Hose Series'}
              value={selectedProduct}
              onChange={handleHoseSelection}
              fullWidth
            >
            {
              HoseSeries.filter(function(option) {
                if (option.label === "P5") {
                  return false; // skip
                }
                return true;
              })
              .map((option) => (
                <MenuItem key={option.label} value={option.value} >
                    {option.label}
                </MenuItem>
                ))
            }
            </TextField>
          </Grid>
          {/* <Grid item xs={12}>
              {
                imgSrc && (
                  <Grid container alignContent="center" alignItems="center" justify="center">
                    <img
                      src={imgSrc || ''}
                      alt="Product"
                      title="Product"
                      style={{height: '100px'}}
                    />
                  </Grid>
                )
              }
          </Grid> */}
          <Grid item xs={12}>
            <TextField 
                select
                label={'Pick Part Number'}
                value={selectedModel}
                onChange={handleModelSelection}
                disabled={!selectedProduct || selectedProduct.legnth < 1}
                fullWidth
              >
              {
                productChoices.filter(function(option) {
                  // if ((option.label === " 7xx-128 ") | (option.label === " 7xx-160 ") | (option.label === " 7xx-192 ") | (option.label === " 816-096 ") |(option.label === " 816-128 ")|(option.label === " 7xx-096 ")|(option.label === " 776-096 ")) {
                  //   return false; // skip
                  // }
                  return true;
                })
                .map((option) => (
                  <MenuItem key={option.label} value={option.value} >
                      {option.label}
                  </MenuItem>
                  ))
              }
              </TextField>
          </Grid>
        </Grid>
      </CardContent>
      <CardContent>
        <ContentContainer>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={12}>
                  <Typography variant="h6">
                    {`Braid Construction`}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    label="# Carr."
                    type="number"
                    value={path('carriers', clonedModel)}
                    onChange={onCarriersChange}
                    InputLabelProps={labelProps}
                    fullWidth
                    InputProps={{
                      inputProps: { 
                          min: 0 
                      }
                  }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="# Wires"
                    type="number"
                    value={path('wires', clonedModel)}
                    onChange={onWiresChange}
                    InputLabelProps={labelProps}
                    fullWidth
                    InputProps={{
                      inputProps: { 
                          min: 0 
                      }
                  }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Wire Diam."
                    type="number"
                    value={path('wireDia', clonedModel)}
                    onChange={onWireDiaChange}
                    InputLabelProps={labelProps}
                    fullWidth
                    InputProps={{
                      inputProps: { 
                          min: 0 
                      }
                  }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Angle (°)"
                    type="number"
                    value={path('angle', clonedModel)}
                    onChange={onAngleChange}
                    InputLabelProps={labelProps}
                    fullWidth
                    InputProps={{
                      inputProps: { 
                          min: 0 
                      }
                  }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Tensile (ksi)"
                    type="number"
                    value={path('tensile', clonedModel)}
                    onChange={onTensileChange}
                    InputLabelProps={labelProps}
                    fullWidth
                    InputProps={{
                      inputProps: { 
                          min: 0 
                      }
                  }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Braid Layers"
                    type="number"
                    value={path('layers', clonedModel)}
                    onChange={onLayersChange}
                    InputLabelProps={labelProps}
                    fullWidth
                    InputProps={{
                      inputProps: { 
                         max:2, min: 0 
                      }
                  }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Grid container spacing={2}>  
                <Grid item xs={12} lg={12}>
                  <Typography variant="h6">
                    {`Pressure @ 70°F (PSIG)`}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    label={`Burst`} 
                    variant="filled" 
                    InputLabelProps={labelProps}
                    disabled 
                    InputProps={inputProps}
                    value={burst} 
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    label={`Test`} 
                    variant="filled" 
                    InputLabelProps={labelProps}
                    disabled 
                    InputProps={inputProps}
                    value={test} 
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    label={`Working`} 
                    variant="filled" 
                    InputLabelProps={labelProps}
                    disabled 
                    InputProps={inputProps}
                    value={working} 
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </ContentContainer>
      </CardContent>
      <CardActions style={{justifyContent: 'flex-end'}}>
        <Button 
          variant="contained"  
          color="grey" 
          disabled={!modelInfo}
          style={
          { float: 'left' }}
          onClick={reset}
        >
          { 'Reset' }
        </Button>
        <Button 
          variant="contained"  
          color="primary" 
          disabled={!modelInfo}
          style={
          { float: 'right' }}
          onClick={recalculate}
        >
          { 'Calculate' }
        </Button>
      </CardActions>
    </ThemeWrapper>
  );
}

export default PressureCalculator;
